// Visit.jsx (React Visit Management with Patient History + Material UI)
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  Grid,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  MenuItem,
  Autocomplete,
  createFilterOptions,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Print,
  Edit,
  Delete,
  Schedule,
  Person,
  LocalHospital,
  Assignment,
  Medication,
  Science,
  Warning,
  PhotoCamera,
} from '@mui/icons-material';
import VisitDataService from '../../services/visit.service';
import ClientDataService from '../../services/client.service';
import http from '../../http-common';
import { formatCurrencySync, initializeCurrency } from '../../utils/currencyUtils';
import { resolveAbsoluteUrl } from '../../utils/assetUrl';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const EXAM_STATUS_ID = {
  ORDERED: '1',
  COMPLETED: '2',
};

/** YYYY-MM-DD in local timezone (avoids UTC off-by-one on date inputs). */
const toLocalDateInputValue = (value = new Date()) => {
  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const examDateFromVisit = (visit) =>
  toLocalDateInputValue(visit?.schedule ? new Date(visit.schedule) : new Date());

/** Single display/edit value: dose per intake plus amount to dispense (legacy quantity column). */
const formatPrescriptionDoseDisplay = (prescription) => {
  if (!prescription) return '';
  const dose = String(prescription.dose ?? '').trim();
  const quantity = String(prescription.quantity ?? '').trim();
  if (dose && quantity) return `${dose} · ${quantity}`;
  return dose || quantity;
};

const visitDialogPaperSx = {
  bgcolor: 'background.default',
  color: 'text.primary',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
};

const visitDialogContentSx = {
  bgcolor: 'background.default',
  flex: '1 1 auto',
  overflowY: 'auto',
  width: '100%',
  boxSizing: 'border-box',
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`visit-tabpanel-${index}`}
      aria-labelledby={`visit-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, sm: 2.5 } }}>{children}</Box>}
    </div>
  );
}

export default function Visit() {
  const { t, language } = useTranslation();
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const contextClientId = Number(queryParams.get('clientId')) || null;
  const contextClientName = queryParams.get('clientName') || '';
  const [visits, setVisits] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [newVisitOpen, setNewVisitOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [visitSaving, setVisitSaving] = useState(false);
  const [visitError, setVisitError] = useState('');
  const [isEditingMedical, setIsEditingMedical] = useState(false);
  const [medicalSaving, setMedicalSaving] = useState(false);
  const [medicalError, setMedicalError] = useState('');
  const [medicalForm, setMedicalForm] = useState({
    dob: '',
    allergies: '',
    medical_summary: '',
  });
  const [visitForm, setVisitForm] = useState({
    client_id: '',
    title: '',
    schedule: '',
    next_appointment_date: '',
    status_id: '',
    motivo: '',
    einicial: '',
    conclusiones: '',
    type_id: 1,
  });

  // Exam management state
  const [exams, setExams] = useState([]);
  const [examForm, setExamForm] = useState({
    id_etype: '',
    id_elist: '',
    id_estatus: EXAM_STATUS_ID.ORDERED,
    reason: '',
    notes: '',
    date: toLocalDateInputValue(),
    priority: 'Routine',
    attachment: null
  });
  const [newExamOpen, setNewExamOpen] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [pendingExams, setPendingExams] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState(null);
  const [examFormError, setExamFormError] = useState('');
  const [examListAlert, setExamListAlert] = useState({ severity: '', message: '' });
  const [visitPrescriptionNotes, setVisitPrescriptionNotes] = useState('');
  const [visitDrugWarnings, setVisitDrugWarnings] = useState([]);
  const [prescriptionContextSaving, setPrescriptionContextSaving] = useState(false);
  const [prescriptionContextMessage, setPrescriptionContextMessage] = useState('');
  const [prescriptionContextError, setPrescriptionContextError] = useState('');

  const examSelectFieldSx = {
    width: '100%',
    '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary', minHeight: 56, width: '100%' },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      maxWidth: 'calc(100% - 24px)',
      whiteSpace: 'normal',
      lineHeight: 1.2,
    },
    '& .MuiInputLabel-shrink': { color: 'text.secondary' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
    '& .MuiSelect-select': { display: 'block', alignItems: 'center' },
    '& .MuiSelect-icon': { color: 'text.secondary' },
    '& .MuiAutocomplete-inputRoot': { width: '100%', flexWrap: 'nowrap' },
  };

  const examAutocompletePopperModifiers = [
    {
      name: 'sameWidth',
      enabled: true,
      phase: 'beforeWrite',
      requires: ['computeStyles'],
      fn: ({ state }) => {
        const width = state.rects.reference.width;
        state.styles.popper.width = `${width}px`;
        state.styles.popper.minWidth = `${width}px`;
      },
    },
  ];

  const examSelectMenuProps = {
    MenuProps: {
      PaperProps: {
        sx: {
          bgcolor: 'background.default',
          color: 'text.primary',
          maxHeight: 320,
          minWidth: '100%',
          '& .MuiMenuItem-root': {
            whiteSpace: 'normal',
            '&:hover': { bgcolor: 'background.paper' },
            '&.Mui-selected': { bgcolor: 'background.paper' },
            '&.Mui-selected:hover': { bgcolor: 'background.paper' },
          },
        },
      },
    },
  };

  // Prescription management state
  const [prescriptions, setPrescriptions] = useState([]);
  const [prescriptionForm, setPrescriptionForm] = useState({
    medication: '',
    dose: '',
    frequency: '',
    duration: '',
    quantity: '',
    instructions: '',
    extra_notes: '',
    status: 'active',
  });
  const [newPrescriptionOpen, setNewPrescriptionOpen] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [clinicLogoUrl, setClinicLogoUrl] = useState(null);
  const [clinicInfo, setClinicInfo] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    nit: '',
    providerName: '',
    providerLicense: '',
  });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoMessage, setPhotoMessage] = useState('');
  const photoInputRef = useRef(null);

  // Invoice management state
  const [invoices, setInvoices] = useState([]);
  const [invoiceForm, setInvoiceForm] = useState({
    header: {
      client_id: '',
      visit_id: '',
      name: 'Johnson Medical Center',
      address: '123 Medical Drive Suite 100, City, State 12345',
      phone: '(555) 987-6543',
      email: 'billing@johnsonmedical.com',
      date: new Date().toISOString().split('T')[0],
      nit: '123456789-0',
      authorization: 'AUTH-2025-001',
      anulada: false,
      reimpresion: false
    },
    details: [
      {
        cantidad: 1,
        descripcion: '',
        precio: 0,
        descuento: 0
      }
    ]
  });
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceCurrencyKey, setInvoiceCurrencyKey] = useState(0);
  const theme = useTheme();
  const providerName = user?.name || t('visitHistory.common.doctor');
  const prescriberName = clinicInfo.providerName || providerName;
  const prescriberLicense = clinicInfo.providerLicense || 'MD-12345';
  const dateLocale = language === 'es' ? 'es' : 'en-US';

  const surfaceCardSx = {
    bgcolor: 'background.paper',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: theme.shadows[1],
  };

  const nestedPanelSx = {
    bgcolor: alpha(theme.palette.primary.main, 0.04),
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    boxShadow: 'none',
  };

  const visitInputSx = {
    '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
    '& .MuiInputLabel-root': { color: 'text.secondary' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  };

  const timelineItemSx = (isSelected, pendingExamCount) => ({
    borderRadius: 2,
    border: '1px solid',
    borderColor: isSelected ? 'primary.main' : 'divider',
    borderLeft: '4px solid',
    borderLeftColor: pendingExamCount > 0
      ? 'warning.main'
      : isSelected
        ? 'primary.main'
        : 'transparent',
    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
    boxShadow: isSelected ? theme.shadows[2] : theme.shadows[1],
    '&:hover': {
      bgcolor: isSelected
        ? alpha(theme.palette.primary.main, 0.12)
        : alpha(theme.palette.primary.main, 0.04),
    },
    '&.Mui-selected': {
      bgcolor: alpha(theme.palette.primary.main, 0.1),
      color: 'text.primary',
    },
    flexDirection: 'column',
    alignItems: 'flex-start',
    p: 2,
  });
  const clinicLogoSrc = useMemo(() => resolveAbsoluteUrl(clinicLogoUrl), [clinicLogoUrl]);

  const renderClinicLogoBox = useCallback(
    (size = 64) => (
      <Box
        sx={{
          width: size,
          height: size,
          minWidth: size,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          p: clinicLogoSrc ? 0.5 : 0,
        }}
      >
        {clinicLogoSrc ? (
          <Box
            component="img"
            src={clinicLogoSrc}
            alt={t('visitHistory.invoiceTab.logoTitle')}
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Person sx={{ fontSize: Math.round(size * 0.5), color: 'text.secondary' }} />
        )}
      </Box>
    ),
    [clinicLogoSrc, t, theme]
  );

  const translateVisitStatus = (statusKey) => {
    const translated = t(`visitHistory.visitStatuses.${statusKey}`);
    return translated !== `visitHistory.visitStatuses.${statusKey}` ? translated : statusKey;
  };

  const getVisitTypeName = (visit) => {
    const typeKeyMap = { 1: 'review', 2: 'emergency', 3: 'followUp', 4: 'consultation' };
    const key = typeKeyMap[visit?.type_id];
    if (key) return t(`visitHistory.visitTypes.${key}`);
    return visit?.type?.name || '-';
  };

  const translatePrescriptionStatus = (status) => {
    const key = String(status || '').toLowerCase();
    const translated = t(`visitHistory.prescriptionTab.medicationStatuses.${key}`);
    return translated !== `visitHistory.prescriptionTab.medicationStatuses.${key}`
      ? translated
      : status;
  };

  const statusColors = {
    scheduled: 'info',
    confirmed: 'warning',
    done: 'success',
    passed: 'default',
    cancelled: 'error',
  };

  const getVisitStatusName = (visit) => {
    const rawStatus = String(visit?.status?.name || '').toLowerCase();
    const scheduleValue = visit?.schedule || visit?.created_at;
    const parsedSchedule = scheduleValue ? new Date(scheduleValue) : null;
    const isPastDate = parsedSchedule && !Number.isNaN(parsedSchedule.getTime()) && parsedSchedule < new Date();
    const hasClinicalInfo = [visit?.motivo, visit?.einicial, visit?.conclusiones]
      .some((value) => String(value || '').trim().length > 0);

    if (visit?._source === 'appointment') {
      if (rawStatus === 'confirmed') return 'confirmed';
      if (rawStatus === 'cancelled') return 'cancelled';
      if (isPastDate) return 'passed';
      return 'scheduled';
    }

    if (['done', 'completed', 'finished', 'closed'].includes(rawStatus)) {
      return 'done';
    }
    if (hasClinicalInfo) {
      return 'done';
    }
    if (rawStatus === 'confirmed') {
      return 'confirmed';
    }
    if (rawStatus === 'cancelled') {
      return 'cancelled';
    }
    if (isPastDate) {
      return 'passed';
    }
    return 'scheduled';
  };

  const buildAppointmentSchedule = (appointment) => {
    const rawDate = String(appointment?.date || '');
    const rawStart = String(appointment?.start_time || '');
    const cleanDate = rawDate.includes('T') ? rawDate.split('T')[0] : rawDate;
    const cleanTime = rawStart.split('.')[0];
    return `${cleanDate}T${cleanTime}`;
  };

  const mapAppointmentToVisit = (appointment) => ({
    id: `appt-${appointment.id}`,
    _source: 'appointment',
    appointment_id: appointment.id,
    client_id: appointment.client_id,
    client: appointment.client,
    title: appointment.visit?.title || t('visitHistory.appointment.plannedTitle'),
    schedule: buildAppointmentSchedule(appointment),
    motivo: appointment.visit?.motivo || t('visitHistory.appointment.scheduledFromCalendar'),
    einicial: '',
    conclusiones: '',
    type_id: null,
    type: appointment.visit?.type || null,
    status: { name: appointment.status || 'pending' },
    created_at: appointment.created_at,
  });

  const filterExamTypeOptions = createFilterOptions({
    stringify: (option) => option.name || '',
  });

  const loadExamTypes = async () => {
    try {
      const response = await VisitDataService.getExamTypes();
      const items = response.data || [];
      setExamTypes(
        [...items].sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }))
      );
    } catch (error) {
      console.error('Error loading exam types:', error);
    }
  };

  useEffect(() => {
    loadVisits();
    loadExamTypes();
    initializeCurrency();
  }, []);

  useEffect(() => {
    const loadClinicLogo = async () => {
      try {
        const response = await http.get('/settings');
        setClinicLogoUrl(response.data?.logo_url || null);
        setClinicInfo({
          name: response.data?.clinic_name || '',
          address: response.data?.clinic_address || '',
          phone: response.data?.clinic_phone || '',
          email: response.data?.clinic_email || '',
          nit: response.data?.clinic_nit || '',
          providerName: response.data?.provider_name || '',
          providerLicense: response.data?.provider_license || '',
        });
      } catch (error) {
        console.error('Error loading clinic logo:', error);
      }
    };
    loadClinicLogo();
  }, []);

  useEffect(() => {
    if (!newInvoiceOpen) return;
    initializeCurrency().then(() => setInvoiceCurrencyKey((key) => key + 1));
  }, [newInvoiceOpen]);

  const loadVisits = async () => {
    try {
      const response = await VisitDataService.getVisits();
      console.log('Visits data:', response.data);
      const allVisits = response.data || [];
      const visitsForContextClient = contextClientId
        ? allVisits.filter(
            (visit) =>
              Number(visit.client_id) === contextClientId ||
              Number(visit.client?.id) === contextClientId
          )
        : allVisits;

      let timelineItems = [...visitsForContextClient];

      const timelineClientId =
        contextClientId ||
        visitsForContextClient[0]?.client_id ||
        visitsForContextClient[0]?.client?.id ||
        null;

      if (timelineClientId) {
        try {
          const appointmentsRes = await VisitDataService.getAppointmentsByClient(timelineClientId);
          const appointments = appointmentsRes.data || [];
          const plannedWithoutVisit = appointments.filter((appointment) => !appointment.visit_id);
          timelineItems = [
            ...timelineItems,
            ...plannedWithoutVisit.map(mapAppointmentToVisit),
          ];
        } catch (appointmentsError) {
          console.error('Error loading planned appointments:', appointmentsError);
        }
      }

      timelineItems.sort((a, b) => new Date(b.schedule || b.created_at) - new Date(a.schedule || a.created_at));
      setVisits(timelineItems);
      
      // Set default patient info from first visit if available
      if (timelineItems.length > 0 && timelineItems[0].client) {
        setCurrentPatient(timelineItems[0].client);
        setSelectedVisit(timelineItems[0]);
        // Load exams, prescriptions, and invoices for the selected visit
        if (timelineItems[0]._source !== 'appointment') {
          await loadExams(timelineItems[0].id);
          await loadPrescriptions(timelineItems[0].id);
          await loadInvoices(timelineItems[0].id);
        } else {
          setExams([]);
          setPrescriptions([]);
          setInvoices([]);
        }
        await loadPendingExams(timelineItems[0].client_id || timelineItems[0].client?.id);
      } else if (contextClientId) {
        setSelectedVisit(null);
        try {
          const clientRes = await ClientDataService.getClient(contextClientId);
          const client = clientRes.data?.data ?? clientRes.data?.client ?? clientRes.data;
          setCurrentPatient(
            client?.id
              ? client
              : {
                  id: contextClientId,
                  name: contextClientName || `Client #${contextClientId}`,
                }
          );
        } catch (clientError) {
          console.error('Error loading client:', clientError);
          setCurrentPatient({
            id: contextClientId,
            name: contextClientName || `Client #${contextClientId}`,
          });
        }
        await loadPendingExams(contextClientId);
      }
    } catch (error) {
      console.error('Error loading visits:', error);
    }
  };

  const resolveClientId = () =>
    currentPatient?.id ||
    selectedVisit?.client_id ||
    selectedVisit?.client?.id ||
    contextClientId ||
    null;

  const loadExams = async (visitId) => {
    if (!visitId || String(visitId).startsWith('appt-')) {
      setExams([]);
      return;
    }
    try {
      const response = await VisitDataService.getExamsByVisit(visitId);
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      setExams(list);
    } catch (error) {
      console.error('Error loading exams:', error);
      setExams([]);
    }
  };

  const loadPendingExams = async (clientId) => {
    const id = clientId || resolveClientId();
    if (!id) {
      setPendingExams([]);
      return;
    }
    try {
      const response = await VisitDataService.getPendingExamsByClient(id);
      setPendingExams(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading pending exams:', error);
      setPendingExams([]);
    }
  };

  const translateExamStatus = (statusName) => {
    const key = String(statusName || '').toLowerCase();
    const translated = t(`visitHistory.exams.statuses.${key}`);
    return translated !== `visitHistory.exams.statuses.${key}` ? translated : statusName || t('visitHistory.exams.unknownStatus');
  };

  const translateExamPriority = (priorityName) => {
    const key = String(priorityName || '').toLowerCase();
    const translated = t(`visitHistory.exams.priorities.${key}`);
    return translated !== `visitHistory.exams.priorities.${key}` ? translated : priorityName || t('visitHistory.exams.unknownPriority');
  };

  const pendingExamsByVisitId = useMemo(() => {
    const map = new Map();
    pendingExams.forEach((exam) => {
      const visitId = exam.visit_id || exam.visit?.id;
      if (!visitId) return;
      const key = Number(visitId);
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [pendingExams]);

  const getPendingExamCountForVisit = (visit) => {
    if (!visit?.id || visit._source === 'appointment') return 0;
    return pendingExamsByVisitId.get(Number(visit.id)) || 0;
  };

  const loadPrescriptions = async (visitId) => {
    if (!visitId || String(visitId).startsWith('appt-')) {
      setPrescriptions([]);
      return;
    }
    try {
      const response = await VisitDataService.getPrescriptionsByVisit(visitId);
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      setPrescriptions(list);
    } catch (error) {
      console.error('Error loading prescriptions:', error);
      setPrescriptions([]);
    }
  };

  const loadInvoices = async (visitId) => {
    try {
      const response = await VisitDataService.getInvoicesByVisit(visitId);
      console.log('Invoices data:', response.data);
      // Ensure we're setting an array, even if the API returns a different structure
      if (response.data && Array.isArray(response.data)) {
        setInvoices(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setInvoices(response.data.data);
      } else {
        console.warn('Unexpected invoices data structure:', response.data);
        setInvoices([]);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
    }
  };

  const parseDrugWarnings = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const loadPrescriptionContextFromVisit = (visit) => {
    setVisitPrescriptionNotes(visit?.prescription_additional_notes || '');
    setVisitDrugWarnings(
      parseDrugWarnings(visit?.drug_interaction_warnings).map((item, index) => ({
        id: `warning-${index}-${item.title || ''}`,
        title: item.title || '',
        detail: item.detail || '',
      }))
    );
    setPrescriptionContextMessage('');
    setPrescriptionContextError('');
  };

  const handleVisitSelect = (visit) => {
    setSelectedVisit(visit);
    setActiveTab(0);
    setEditMode(false);
    if (visit._source === 'appointment') {
      setExams([]);
      setPrescriptions([]);
      setInvoices([]);
      setVisitPrescriptionNotes('');
      setVisitDrugWarnings([]);
      loadPendingExams(visit.client_id || visit.client?.id);
      return;
    }
    loadPrescriptionContextFromVisit(visit);
    loadExams(visit.id);
    loadPrescriptions(visit.id);
    loadInvoices(visit.id);
    loadPendingExams(visit.client_id || visit.client?.id);
  };

  const addDrugWarning = () => {
    setVisitDrugWarnings((prev) => [
      ...prev,
      { id: `warning-${Date.now()}`, title: '', detail: '' },
    ]);
  };

  const updateDrugWarning = (id, field, value) => {
    setVisitDrugWarnings((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const removeDrugWarning = (id) => {
    setVisitDrugWarnings((prev) => prev.filter((item) => item.id !== id));
  };

  const savePrescriptionContext = async () => {
    if (!selectedVisit?.id || selectedVisit._source === 'appointment') {
      setPrescriptionContextError(t('visitHistory.prescriptions.visitRequired'));
      return;
    }

    setPrescriptionContextSaving(true);
    setPrescriptionContextError('');
    setPrescriptionContextMessage('');

    try {
      const payload = {
        prescription_additional_notes: visitPrescriptionNotes.trim() || null,
        drug_interaction_warnings: visitDrugWarnings
          .map(({ title, detail }) => ({
            title: String(title || '').trim(),
            detail: String(detail || '').trim(),
          }))
          .filter((item) => item.title || item.detail),
      };

      const response = await VisitDataService.updateVisit(selectedVisit.id, payload);
      const updated = response.data;
      setSelectedVisit((prev) => ({ ...prev, ...updated }));
      loadPrescriptionContextFromVisit(updated);
      setPrescriptionContextMessage(t('visitHistory.prescriptions.saved'));
      setVisits((prev) =>
        prev.map((visit) => (visit.id === updated.id ? { ...visit, ...updated } : visit))
      );
    } catch (error) {
      console.error('Error saving prescription context:', error);
      setPrescriptionContextError(
        error?.response?.data?.message || t('visitHistory.prescriptions.saveFailed')
      );
    } finally {
      setPrescriptionContextSaving(false);
    }
  };

  const handleExamFormChange = (e) => {
    const { name, value } = e.target;
    setExamForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'notes' && String(value).trim()) {
        next.id_estatus = EXAM_STATUS_ID.COMPLETED;
      }
      return next;
    });
  };

  const getExamTypeOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    if (option?.inputValue) return option.inputValue;
    return option?.name || '';
  };

  const handleExamTypeChange = async (_, value) => {
    setExamFormError('');

    if (!value) {
      setSelectedExamType(null);
      setExamForm((prev) => ({ ...prev, id_elist: '' }));
      return;
    }

    if (value.inputValue) {
      try {
        const response = await VisitDataService.createExamType({ name: value.inputValue.trim() });
        const created = response.data?.data || response.data;
        setExamTypes((prev) => {
          const exists = prev.some((item) => item.id === created.id);
          const next = exists ? prev : [...prev, created];
          return next.sort((a, b) =>
            String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
          );
        });
        setSelectedExamType(created);
        setExamForm((prev) => ({ ...prev, id_elist: String(created.id) }));
      } catch (error) {
        console.error('Error creating exam type:', error);
        setExamFormError(t('visitHistory.exams.createExamTypeFailed'));
      }
      return;
    }

    setSelectedExamType(value);
    setExamForm((prev) => ({ ...prev, id_elist: String(value.id) }));
  };

  const resolveExamTypeId = async () => {
    if (examForm.id_elist) {
      return Number(examForm.id_elist);
    }

    const typedName =
      typeof selectedExamType === 'string'
        ? selectedExamType.trim()
        : selectedExamType?.name?.trim();

    if (!typedName) {
      return null;
    }

    const existing = examTypes.find(
      (item) => String(item.name).toLowerCase() === typedName.toLowerCase()
    );
    if (existing) {
      return existing.id;
    }

    const response = await VisitDataService.createExamType({ name: typedName });
    const created = response.data?.data || response.data;
    setExamTypes((prev) => {
      const exists = prev.some((item) => item.id === created.id);
      const next = exists ? prev : [...prev, created];
      return next.sort((a, b) =>
        String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
      );
    });
    setSelectedExamType(created);
    setExamForm((prev) => ({ ...prev, id_elist: String(created.id) }));
    return created.id;
  };

  const openNewExamForm = () => {
    if (!selectedVisit?.id || selectedVisit._source === 'appointment') {
      setExamListAlert({
        severity: 'warning',
        message: t('visitHistory.exams.visitRequiredForExam'),
      });
      return;
    }
    setExamListAlert({ severity: '', message: '' });
    setExamForm({
      id_etype: '',
      id_elist: '',
      id_estatus: EXAM_STATUS_ID.ORDERED,
      reason: '',
      notes: '',
      date: examDateFromVisit(selectedVisit),
      priority: 'Routine',
      attachment: null
    });
    setSelectedExamType(null);
    setExamFormError('');
    setEditingExam(null);
    loadExamTypes();
    setNewExamOpen(true);
  };

  const buildExamPayload = (idElist) => ({
    visit_id: selectedVisit?.id,
    id_etype: examForm.id_etype ? Number(examForm.id_etype) : 2,
    id_elist: idElist ?? (examForm.id_elist ? Number(examForm.id_elist) : null),
    id_estatus: String(examForm.notes || '').trim()
      ? Number(EXAM_STATUS_ID.COMPLETED)
      : examForm.id_estatus
        ? Number(examForm.id_estatus)
        : Number(EXAM_STATUS_ID.ORDERED),
    reason: examForm.reason || null,
    notes: examForm.notes || null,
    date: examForm.date || null,
  });

  const formatExamSaveError = (error) => {
    const data = error?.response?.data;
    if (data?.message) return data.message;
    if (data?.errors) {
      return Object.values(data.errors).flat().join(' ');
    }
    return t('visitHistory.exams.saveFailed');
  };

  const saveExam = async () => {
    if (!selectedVisit?.id || selectedVisit._source === 'appointment') {
      setExamFormError(t('visitHistory.exams.visitRequiredForExam'));
      return;
    }

    try {
      setExamFormError('');
      setExamListAlert({ severity: '', message: '' });
      const idElist = await resolveExamTypeId();
      if (!idElist) {
        setExamFormError(t('visitHistory.exams.examTypeRequired'));
        return;
      }

      const payload = buildExamPayload(idElist);
      if (editingExam) {
        const { visit_id, ...updatePayload } = payload;
        await VisitDataService.updateExam(editingExam.id, updatePayload);
      } else {
        await VisitDataService.createExam(payload);
      }
      setNewExamOpen(false);
      setEditingExam(null);
      setSelectedExamType(null);
      await loadExams(selectedVisit.id);
      await loadPendingExams();
      loadExamTypes();
      setExamListAlert({
        severity: 'success',
        message: t('visitHistory.exams.saved'),
      });
    } catch (error) {
      console.error('Error saving exam:', error);
      const message = formatExamSaveError(error);
      setExamFormError(message);
      setExamListAlert({ severity: 'error', message });
    }
  };

  const editExam = (exam) => {
    setExamForm({
      id_etype: exam.id_etype != null ? String(exam.id_etype) : '',
      id_elist: exam.id_elist != null ? String(exam.id_elist) : '',
      id_estatus:
        exam.id_estatus != null
          ? String(exam.id_estatus) === '3'
            ? exam.notes?.trim()
              ? EXAM_STATUS_ID.COMPLETED
              : EXAM_STATUS_ID.ORDERED
            : String(exam.id_estatus)
          : EXAM_STATUS_ID.ORDERED,
      reason: exam.reason,
      notes: exam.notes,
      date: exam.date ? toLocalDateInputValue(exam.date) : examDateFromVisit(selectedVisit),
      priority: exam.priority || 'Routine',
      attachment: exam.attachment
    });
    setSelectedExamType(
      exam.elist ||
        examTypes.find((item) => String(item.id) === String(exam.id_elist)) ||
        null
    );
    setExamFormError('');
    setEditingExam(exam);
    loadExamTypes();
    setNewExamOpen(true);
  };

  const deleteExam = async (examId) => {
    if (window.confirm(t('visitHistory.exams.deleteConfirm'))) {
      try {
        await VisitDataService.deleteExam(examId);
        loadExams(selectedVisit.id);
        loadPendingExams();
      } catch (error) {
        console.error('Error deleting exam:', error);
      }
    }
  };

  // Prescription form handling
  const handlePrescriptionFormChange = (e) => {
    const { name, value } = e.target;
    setPrescriptionForm(prev => ({ ...prev, [name]: value }));
  };

  const openNewPrescriptionForm = () => {
    setPrescriptionForm({
      medication: '',
      dose: '',
      frequency: '',
      duration: '',
      quantity: '',
      instructions: '',
      extra_notes: '',
      status: 'active',
    });
    setEditingPrescription(null);
    setNewPrescriptionOpen(true);
  };

  const savePrescription = async () => {
    if (!selectedVisit?.id || selectedVisit._source === 'appointment') {
      console.error('Cannot save prescription without a completed visit');
      return;
    }
    if (!prescriptionForm.medication?.trim()) {
      return;
    }
    try {
      const payload = { ...prescriptionForm, quantity: prescriptionForm.quantity || '' };
      if (editingPrescription) {
        await VisitDataService.updatePrescription(editingPrescription.id, payload);
      } else {
        await VisitDataService.createPrescriptionForVisit(selectedVisit.id, payload);
      }
      setNewPrescriptionOpen(false);
      setEditingPrescription(null);
      await loadPrescriptions(selectedVisit.id);
    } catch (error) {
      console.error('Error saving prescription:', error);
    }
  };

  const editPrescription = (prescription) => {
    setPrescriptionForm({
      medication: prescription.medication,
      dose: formatPrescriptionDoseDisplay(prescription),
      frequency: prescription.frequency || '',
      duration: prescription.duration || '',
      quantity: '',
      instructions: prescription.instructions || '',
      extra_notes: prescription.extra_notes || '',
      status: prescription.status,
    });
    setEditingPrescription(prescription);
    setNewPrescriptionOpen(true);
  };

  const deletePrescription = async (prescriptionId) => {
    if (window.confirm(t('visitHistory.prescriptionTab.dialog.deleteConfirm'))) {
      try {
        await VisitDataService.deletePrescription(prescriptionId);
        loadPrescriptions(selectedVisit.id);
      } catch (error) {
        console.error('Error deleting prescription:', error);
      }
    }
  };

  // Invoice form handling
  const handleInvoiceFormChange = (section, field, value, index = null) => {
    if (section === 'header') {
      setInvoiceForm(prev => ({
        ...prev,
        header: { ...prev.header, [field]: value }
      }));
    } else if (section === 'details' && index !== null) {
      setInvoiceForm(prev => ({
        ...prev,
        details: prev.details.map((detail, i) => 
          i === index ? { ...detail, [field]: value } : detail
        )
      }));
    }
  };

  const addInvoiceDetail = () => {
    setInvoiceForm(prev => ({
      ...prev,
      details: [...prev.details, {
        cantidad: 1,
        descripcion: '',
        precio: 0,
        descuento: 0
      }]
    }));
  };

  const removeInvoiceDetail = (index) => {
    if (invoiceForm.details.length > 1) {
      setInvoiceForm(prev => ({
        ...prev,
        details: prev.details.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateInvoiceTotals = () => {
    const subtotal = invoiceForm.details.reduce(
      (sum, detail) => sum + detail.cantidad * detail.precio,
      0
    );
    const totalDiscount = invoiceForm.details.reduce((sum, detail) => {
      const lineTotal = detail.cantidad * detail.precio;
      return sum + lineTotal * (detail.descuento / 100);
    }, 0);
    const total = subtotal - totalDiscount;

    return { subtotal, totalDiscount, total };
  };

  const formatInvoiceAmount = (amount) => formatCurrencySync(amount);

  const getInvoiceLineTotal = (detail) => {
    const lineTotal = detail.cantidad * detail.precio;
    return lineTotal - lineTotal * (detail.descuento / 100);
  };

  const openNewInvoiceForm = () => {
    initializeCurrency();
    setInvoiceForm({
      header: {
        client_id: currentPatient?.id || '',
        visit_id: selectedVisit?.id || '',
        name: clinicInfo.name || 'Johnson Medical Center',
        address: clinicInfo.address || '123 Medical Drive Suite 100, City, State 12345',
        phone: clinicInfo.phone || '(555) 987-6543',
        email: clinicInfo.email || 'billing@johnsonmedical.com',
        date: new Date().toISOString().split('T')[0],
        nit: clinicInfo.nit || '123456789-0',
        authorization: 'AUTH-2025-001',
        anulada: false,
        reimpresion: false
      },
      details: [
        {
          cantidad: 1,
          descripcion: '',
          precio: 0,
          descuento: 0
        }
      ]
    });
    setEditingInvoice(null);
    setNewInvoiceOpen(true);
  };

  const saveInvoice = async () => {
    try {
      const invoiceData = {
        header: {
          ...invoiceForm.header,
          client_id: currentPatient?.id || invoiceForm.header.client_id,
          visit_id: selectedVisit?.id || invoiceForm.header.visit_id
        },
        details: invoiceForm.details
      };

      if (editingInvoice) {
        await VisitDataService.updateInvoice(editingInvoice.id, invoiceData);
      } else {
        await VisitDataService.createInvoice(invoiceData);
      }
      
      setNewInvoiceOpen(false);
      setEditingInvoice(null);
      loadInvoices(selectedVisit.id);
    } catch (error) {
      console.error('Error saving invoice:', error);
    }
  };

  const editInvoice = (invoice) => {
    initializeCurrency();
    setInvoiceForm({
      header: {
        client_id: invoice.client_id || '',
        visit_id: invoice.visit_id || '',
        name: invoice.name || 'Johnson Medical Center',
        address: invoice.address || '123 Medical Drive Suite 100, City, State 12345',
        phone: invoice.phone || '(555) 987-6543',
        email: invoice.email || 'billing@johnsonmedical.com',
        date: invoice.date || new Date().toISOString().split('T')[0],
        nit: invoice.nit || '123456789-0',
        authorization: invoice.authorization || 'AUTH-2025-001',
        anulada: invoice.anulada || false,
        reimpresion: invoice.reimpresion || false
      },
      details: invoice.idetails?.map(detail => ({
        cantidad: detail.cantidad,
        descripcion: detail.descripcion,
        precio: detail.precio,
        descuento: detail.descuento
      })) || [
        {
          cantidad: 1,
          descripcion: '',
          precio: 0,
          descuento: 0
        }
      ]
    });
    setEditingInvoice(invoice);
    setNewInvoiceOpen(true);
  };

  const deleteInvoice = async (invoiceId) => {
    if (window.confirm(t('visitHistory.invoiceTab.dialog.deleteConfirm'))) {
      try {
        await VisitDataService.deleteInvoice(invoiceId);
        loadInvoices(selectedVisit.id);
      } catch (error) {
        console.error('Error deleting invoice:', error);
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setVisitForm(prev => ({ ...prev, [name]: value }));
  };

  const saveVisit = async () => {
    try {
      setVisitSaving(true);
      setVisitError('');
      const payload = {
        client_id: visitForm.client_id || currentPatient?.id || contextClientId,
        title: String(visitForm.title || '').trim(),
        schedule: visitForm.schedule || null,
        next_appointment_date: visitForm.next_appointment_date || null,
        motivo: String(visitForm.motivo || '').trim(),
        einicial: String(visitForm.einicial || '').trim(),
        conclusiones: String(visitForm.conclusiones || '').trim(),
        type_id: visitForm.type_id || 1,
        status_id: visitForm.status_id || null,
      };

      if (!payload.title || !payload.schedule) {
        setVisitError(t('visitHistory.form.requiredTitleSchedule'));
        return;
      }

      if (Number.isNaN(Date.parse(payload.schedule))) {
        setVisitError(t('visitHistory.form.invalidSchedule'));
        return;
      }

      const normalizedPayload = {
        client_id: visitForm.client_id || currentPatient?.id || contextClientId,
        ...payload,
      };

      if (!normalizedPayload.client_id) {
        setVisitError(t('visitHistory.form.clientRequired'));
        return;
      }

      if (editMode && selectedVisit) {
        await VisitDataService.updateVisit(selectedVisit.id, normalizedPayload);
      } else {
        await VisitDataService.createVisit(normalizedPayload);
      }
      setNewVisitOpen(false);
      setEditMode(false);
      setVisitForm({
        client_id: '',
        title: '',
        schedule: '',
        next_appointment_date: '',
        status_id: '',
        motivo: '',
        einicial: '',
        conclusiones: '',
        type_id: 1,
      });
      loadVisits();
    } catch (error) {
      console.error('Error saving visit:', error);
      const message =
        error?.response?.data?.message ||
        (error?.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(' ')
          : '') ||
        t('visitHistory.errors.saveVisit');
      setVisitError(message);
    } finally {
      setVisitSaving(false);
    }
  };

  const startEditVisit = () => {
    if (selectedVisit?._source === 'appointment') {
      return;
    }
    if (selectedVisit) {
      setVisitForm({
        client_id: selectedVisit.client?.id || '',
        title: selectedVisit.title || '',
        schedule: selectedVisit.schedule
          ? new Date(selectedVisit.schedule).toISOString().slice(0, 16)
          : '',
        next_appointment_date: selectedVisit.next_appointment_date
          ? String(selectedVisit.next_appointment_date).slice(0, 10)
          : '',
        status_id: selectedVisit.status_id || selectedVisit.status?.id || '',
        motivo: selectedVisit.motivo || '',
        einicial: selectedVisit.einicial || '',
        conclusiones: selectedVisit.conclusiones || '',
        type_id: selectedVisit.type_id || 1,
      });
      setEditMode(true);
      setNewVisitOpen(true);
    }
  };

  const openNewVisit = () => {
    setEditMode(false);
    setVisitForm({
      client_id: currentPatient?.id || contextClientId || '',
      title: '',
      schedule: '',
      next_appointment_date: '',
      status_id: '',
      motivo: '',
      einicial: '',
      conclusiones: '',
      type_id: 1,
    });
    setNewVisitOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const locale = dateLocale;
    const raw = String(dateString).trim();
    const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnly) {
      const [, y, m, d] = dateOnly;
      const local = new Date(Number(y), Number(m) - 1, Number(d));
      return local.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString(dateLocale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const escapeHtml = (value) => {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  const printFieldBlock = (label, value) => {
    const text = String(value ?? '').trim();
    if (!text) return '';
    return `<p class="field"><span class="label">${escapeHtml(label)}:</span> ${escapeHtml(text).replace(/\n/g, '<br>')}</p>`;
  };

  const buildPrintHtml = (title, sections) => {
    const sectionHtml = sections
      .filter((section) => section && (section.heading || section.body))
      .map(
        (section) => `
        <section class="print-section">
          ${section.heading ? `<h2>${escapeHtml(section.heading)}</h2>` : ''}
          ${section.body || ''}
        </section>`
      )
      .join('');
    return `<!DOCTYPE html>
<html lang="${language === 'es' ? 'es' : 'en'}">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { font-family: system-ui, -apple-system, sans-serif; color: #111; margin: 24px; line-height: 1.45; }
    h1 { font-size: 1.35rem; margin: 0 0 4px; }
    h2 { font-size: 1rem; margin: 20px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
    .meta { color: #444; font-size: 0.9rem; margin-bottom: 16px; }
    .field { margin: 6px 0; }
    .label { font-weight: 600; }
    ul { margin: 8px 0; padding-left: 20px; }
    li { margin-bottom: 10px; }
    .med-name { font-weight: 600; }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p class="meta">${escapeHtml(new Date().toLocaleString(dateLocale))}</p>
  ${sectionHtml}
</body>
</html>`;
  };

  const printHtmlViaHiddenFrame = (html) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'print');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    document.body.appendChild(iframe);
    const frameWindow = iframe.contentWindow;
    const doc = frameWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    const cleanup = () => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };
    frameWindow.addEventListener('afterprint', cleanup);
    frameWindow.focus();
    setTimeout(() => {
      frameWindow.print();
      setTimeout(cleanup, 2000);
    }, 300);
  };

  const buildPrescriptionPrintHtml = ({
    logoUrl,
    clinicName,
    providerName: rxProviderName,
    providerLicense: rxProviderLicense,
    dateText,
    medications,
    additionalNotes,
  }) => {
    const rxDetailRow = (label, value) => {
      const text = String(value ?? '').trim();
      if (!text) return '';
      return `<div class="rx-row"><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(text).replace(/\n/g, '<br>')}</dd></div>`;
    };

    const medicationsHtml =
      medications.length > 0
        ? medications
            .map((rx, index) => {
              const dose = formatPrescriptionDoseDisplay(rx);
              return `
              <article class="rx-card">
                <header class="rx-card-head">
                  <span class="rx-index">${index + 1}</span>
                  <h3 class="rx-name">${escapeHtml(rx.medication)}</h3>
                </header>
                <dl class="rx-details">
                  ${rxDetailRow(t('visitHistory.prescriptionTab.dosage'), dose)}
                  ${rxDetailRow(t('visitHistory.prescriptionTab.frequency'), rx.frequency)}
                  ${rxDetailRow(t('visitHistory.prescriptionTab.duration'), rx.duration)}
                  ${rxDetailRow(t('visitHistory.prescriptionTab.dialog.instructions'), rx.instructions)}
                  ${rxDetailRow(t('visitHistory.prescriptionTab.dialog.extraNotes'), rx.extra_notes)}
                </dl>
              </article>`;
            })
            .join('')
        : `<p class="rx-empty">${escapeHtml(t('visitHistory.prescriptionTab.empty'))}</p>`;

    const logoBlock = logoUrl
      ? `<img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(clinicName)}" class="rx-logo" />`
      : `<div class="rx-logo-fallback" aria-hidden="true">✚</div>`;

    const notesBlock = additionalNotes
      ? `<section class="rx-notes">
          <h2>${escapeHtml(t('visitHistory.prescriptions.additionalNotesTitle'))}</h2>
          <p>${escapeHtml(additionalNotes).replace(/\n/g, '<br>')}</p>
        </section>`
      : '';

    const doctorLine = [
      String(rxProviderName ?? '').trim(),
      String(rxProviderLicense ?? '').trim()
        ? `${t('visitHistory.common.license')}: ${String(rxProviderLicense).trim()}`
        : '',
    ]
      .filter(Boolean)
      .join(' • ');
    const doctorBlock =
      doctorLine || dateText
        ? `<div class="rx-meta">
            ${doctorLine ? `<span>${escapeHtml(doctorLine)}</span>` : ''}
            ${dateText ? `<span>${escapeHtml(t('visitHistory.common.date'))}: ${escapeHtml(dateText)}</span>` : ''}
          </div>`
        : '';

    const title = t('visitHistory.prescriptionTab.title');

    return `<!DOCTYPE html>
<html lang="${language === 'es' ? 'es' : 'en'}">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    body {
      margin: 0;
      padding: 32px 20px;
      font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
      color: #0f172a;
      background: linear-gradient(160deg, #f0fdfa 0%, #f8fafc 45%, #ffffff 100%);
      line-height: 1.5;
    }
    .rx-page {
      max-width: 680px;
      margin: 0 auto;
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 12px 40px rgba(15, 118, 110, 0.12);
      overflow: hidden;
      border: 1px solid #ccfbf1;
    }
    .rx-header {
      padding: 28px 32px 20px;
      text-align: center;
      background: linear-gradient(180deg, #f0fdfa 0%, #ffffff 100%);
      border-bottom: 2px solid #14b8a6;
    }
    .rx-logo {
      max-height: 88px;
      max-width: 280px;
      width: auto;
      object-fit: contain;
      margin-bottom: 12px;
    }
    .rx-logo-fallback {
      width: 72px;
      height: 72px;
      margin: 0 auto 12px;
      border-radius: 50%;
      background: #0d9488;
      color: #fff;
      font-size: 2rem;
      line-height: 72px;
      font-weight: 700;
    }
    .rx-clinic-name {
      margin: 0 0 4px;
      font-size: 0.85rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #0f766e;
      font-weight: 600;
    }
    .rx-title {
      margin: 0;
      font-size: 1.35rem;
      font-weight: 700;
      color: #134e4a;
      letter-spacing: -0.02em;
    }
    .rx-meta {
      margin-top: 8px;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 4px 16px;
      font-size: 0.85rem;
      color: #475569;
    }
    .rx-body { padding: 24px 32px 32px; }
    .rx-card {
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 16px 18px;
      margin-bottom: 14px;
      background: #fafafa;
    }
    .rx-card:last-child { margin-bottom: 0; }
    .rx-card-head {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 10px;
    }
    .rx-index {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: #0d9488;
      color: #fff;
      font-size: 0.8rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .rx-name {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 700;
      color: #0f172a;
    }
    .rx-details { margin: 0; }
    .rx-row {
      display: grid;
      grid-template-columns: minmax(100px, 32%) 1fr;
      gap: 4px 12px;
      margin: 6px 0;
      font-size: 0.9rem;
    }
    .rx-row dt {
      margin: 0;
      font-weight: 600;
      color: #64748b;
    }
    .rx-row dd { margin: 0; color: #1e293b; }
    .rx-empty {
      text-align: center;
      color: #64748b;
      padding: 24px;
      margin: 0;
    }
    .rx-notes {
      margin-top: 20px;
      padding: 16px 18px;
      border-radius: 12px;
      background: #fffbeb;
      border: 1px solid #fde68a;
    }
    .rx-notes h2 {
      margin: 0 0 8px;
      font-size: 0.8rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #b45309;
    }
    .rx-notes p { margin: 0; font-size: 0.9rem; color: #78350f; }
    @media print {
      body { background: #fff; padding: 0; }
      .rx-page { box-shadow: none; border: none; border-radius: 0; max-width: none; }
    }
  </style>
</head>
<body>
  <div class="rx-page">
    <header class="rx-header">
      ${logoBlock}
      ${!logoUrl ? `<p class="rx-clinic-name">${escapeHtml(clinicName)}</p>` : ''}
      <h1 class="rx-title">${escapeHtml(title)}</h1>
      ${doctorBlock}
    </header>
    <main class="rx-body">
      ${medicationsHtml}
      ${notesBlock}
    </main>
  </div>
</body>
</html>`;
  };

  const openPreviewHtml = (html) => {
    try {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const objectUrl = URL.createObjectURL(blob);
      const previewWindow = window.open(objectUrl, '_blank');
      if (!previewWindow) {
        URL.revokeObjectURL(objectUrl);
        window.alert(t('visitHistory.printPopupBlocked'));
        return;
      }
      previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(objectUrl));
      setTimeout(() => URL.revokeObjectURL(objectUrl), 300000);
    } catch (error) {
      console.error('Preview error:', error);
    }
  };

  const openPrintHtml = (html) => {
    let objectUrl = null;

    const revokeUrl = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };

    try {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      objectUrl = URL.createObjectURL(blob);
      const printWindow = window.open(objectUrl, '_blank');
      if (!printWindow) {
        revokeUrl();
        window.alert(t('visitHistory.printPopupBlocked'));
        printHtmlViaHiddenFrame(html);
        return;
      }

      const triggerPrint = () => {
        printWindow.focus();
        setTimeout(() => {
          try {
            printWindow.print();
          } catch (error) {
            console.error('Print failed:', error);
          }
        }, 150);
      };

      printWindow.addEventListener('load', triggerPrint);
      // If load already fired (cached blob)
      if (printWindow.document?.readyState === 'complete') {
        triggerPrint();
      }

      printWindow.addEventListener('afterprint', () => {
        revokeUrl();
        try {
          printWindow.close();
        } catch {
          /* ignore */
        }
      });
      printWindow.addEventListener('beforeunload', revokeUrl);
      setTimeout(revokeUrl, 120000);
    } catch (error) {
      console.error('Print document error:', error);
      revokeUrl();
      printHtmlViaHiddenFrame(html);
    }
  };

  const openPrintDocument = (title, sections) => {
    openPrintHtml(buildPrintHtml(title, sections));
  };

  const buildPatientPrintBody = () => {
    const detail = currentPatient?.medical_detail || currentPatient?.medicalDetail || null;
    const allergies = detail?.allergies || detail?.notes || '';
    return `
      ${printFieldBlock(t('visitHistory.common.id'), currentPatient?.id)}
      ${printFieldBlock(t('visitHistory.dob'), detail?.dob ? formatDate(detail.dob) : '')}
      ${printFieldBlock(t('visitHistory.phone'), currentPatient?.phone || currentPatient?.telefono)}
      ${printFieldBlock(t('visitHistory.medicalSummary'), detail?.summary || detail?.resumen)}
      ${printFieldBlock(t('visitHistory.allergiesNotes'), allergies)}
    `;
  };

  const buildVisitMetaPrintBody = (visit) => `
    ${printFieldBlock(t('visitHistory.visitType'), visit?.type?.name || getVisitTypeName(visit))}
    ${printFieldBlock(t('visitHistory.status'), translateVisitStatus(getVisitStatusName(visit)))}
    ${printFieldBlock(t('visitHistory.common.date'), formatDate(visit?.schedule))}
    ${printFieldBlock(t('visitHistory.printTime'), formatTime(visit?.schedule))}
    ${printFieldBlock(t('visitHistory.common.doctor'), providerName)}
  `;

  const handlePrintExams = () => {
    if (!selectedVisit?.id || selectedVisit._source === 'appointment') return;
    const visitTitle = `${selectedVisit.title || getVisitTypeName(selectedVisit)} - ${formatDate(selectedVisit.schedule)}`;
    const examListHtml =
      exams.length > 0
        ? `<ul>${exams
            .map(
              (exam) => `
            <li>
              <span class="med-name">${escapeHtml(exam.elist?.name || t('visitHistory.exams.unknownExam'))}</span>
              — ${escapeHtml(translateExamStatus(exam.estatus?.name))}
              <br>${escapeHtml(t('visitHistory.exams.requested'))}: ${escapeHtml(formatDate(exam.date))}
              ${exam.reason ? `<br>${escapeHtml(t('visitHistory.exams.noReason'))}: ${escapeHtml(exam.reason)}` : ''}
              ${exam.notes ? `<br>${escapeHtml(t('visitHistory.exams.notes'))}: ${escapeHtml(exam.notes)}` : ''}
            </li>`
            )
            .join('')}</ul>`
        : `<p>${escapeHtml(t('visitHistory.exams.empty'))}</p>`;
    openPrintDocument(`${t('visitHistory.exams.printOrders')} — ${visitTitle}`, [
      { heading: currentPatient?.name, body: buildPatientPrintBody() },
      { heading: visitTitle, body: buildVisitMetaPrintBody(selectedVisit) },
      { heading: t('visitHistory.exams.prescribedForVisit'), body: examListHtml },
    ]);
  };

  const buildPrescriptionPrintDocumentHtml = async () => {
    if (!selectedVisit?.id || selectedVisit._source === 'appointment') return null;

    let logoUrl = clinicLogoUrl;
    if (!logoUrl) {
      try {
        const response = await http.get('/settings');
        logoUrl = response.data?.logo_url || null;
        if (logoUrl) setClinicLogoUrl(logoUrl);
      } catch (error) {
        console.error('Error loading clinic logo:', error);
      }
    }

    return buildPrescriptionPrintHtml({
      logoUrl: resolveAbsoluteUrl(logoUrl),
      clinicName: clinicInfo.name || t('visitHistory.prescriptionTab.clinicName'),
      providerName: prescriberName,
      providerLicense: prescriberLicense,
      dateText: selectedVisit?.schedule ? formatDate(selectedVisit.schedule) : '',
      medications: prescriptions,
      additionalNotes: visitPrescriptionNotes?.trim() || '',
    });
  };

  const handlePreviewPrescriptions = async () => {
    const html = await buildPrescriptionPrintDocumentHtml();
    if (!html) return;
    openPreviewHtml(html);
  };

  const handlePrintPrescriptions = async () => {
    const html = await buildPrescriptionPrintDocumentHtml();
    if (!html) return;
    openPrintHtml(html);
  };

  const medicalDetail = currentPatient?.medical_detail || currentPatient?.medicalDetail || null;
  const patientPhotoUrl =
    medicalDetail?.photo_url || medicalDetail?.photoUrl || null;
  const patientHasCustomPhoto = Boolean(patientPhotoUrl);

  const patientPhotoSrc = useMemo(() => {
    if (patientPhotoUrl) {
      return resolveAbsoluteUrl(patientPhotoUrl);
    }
    return clinicLogoSrc || null;
  }, [patientPhotoUrl, clinicLogoSrc]);

  const applyClientToState = useCallback((client) => {
    if (!client?.id) return;
    setCurrentPatient(client);
    setVisits((prev) =>
      prev.map((visit) => {
        const visitClientId = visit.client_id ?? visit.client?.id;
        if (Number(visitClientId) !== Number(client.id)) {
          return visit;
        }
        return { ...visit, client };
      })
    );
  }, []);

  const handlePatientPhotoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentPatient?.id) return;

    if (!file.type.startsWith('image/')) {
      setPhotoMessage('');
      setMedicalError(t('visitHistory.errors.uploadPhoto'));
      return;
    }

    try {
      setPhotoUploading(true);
      setPhotoMessage('');
      setMedicalError('');
      const response = await ClientDataService.uploadPhoto(currentPatient.id, file);
      const client = response.data?.client;
      if (client) {
        applyClientToState(client);
      }
      setPhotoMessage(t('visitHistory.photoUploadSuccess'));
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        (error?.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(' ')
          : '') ||
        t('visitHistory.errors.uploadPhoto');
      setMedicalError(message);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleRemovePatientPhoto = async () => {
    if (!currentPatient?.id || !patientHasCustomPhoto) return;
    if (!window.confirm(t('visitHistory.confirmRemovePhoto'))) return;

    try {
      setPhotoUploading(true);
      setPhotoMessage('');
      setMedicalError('');
      const response = await ClientDataService.removePhoto(currentPatient.id);
      const client = response.data?.client;
      if (client) {
        applyClientToState(client);
      }
      setPhotoMessage(t('visitHistory.photoRemovedSuccess'));
    } catch (error) {
      setMedicalError(
        error?.response?.data?.message || t('visitHistory.errors.removePhoto')
      );
    } finally {
      setPhotoUploading(false);
    }
  };

  useEffect(() => {
    if (!currentPatient) return;
    setMedicalForm({
      dob: medicalDetail?.dob || '',
      allergies: medicalDetail?.allergies || '',
      medical_summary: medicalDetail?.medical_summary || '',
    });
  }, [currentPatient, medicalDetail?.dob, medicalDetail?.allergies, medicalDetail?.medical_summary]);

  const saveMedicalDetail = async () => {
    if (!currentPatient?.id) return;
    try {
      setMedicalSaving(true);
      setMedicalError('');
      await ClientDataService.updateClient(currentPatient.id, {
        name: currentPatient.name,
        phone: currentPatient.phone || '',
        email: currentPatient.email || '',
        medical_detail: {
          dob: medicalForm.dob || null,
          allergies: medicalForm.allergies || null,
          medical_summary: medicalForm.medical_summary || null,
        }
      });
      setIsEditingMedical(false);
      await loadVisits();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        (error?.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(' ')
          : '') ||
        t('visitHistory.errors.saveMedical');
      setMedicalError(message);
    } finally {
      setMedicalSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{ ...surfaceCardSx, borderRadius: 0, borderLeft: 0, borderRight: 0, borderTop: 0, boxShadow: 'none' }}
      >
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton sx={{ color: 'text.secondary' }} onClick={() => navigate('/clients')}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" sx={{ color: 'text.primary' }}>
              {currentPatient?.name ? `${currentPatient.name} - ${t('visitHistory.title')}` : t('visitHistory.title')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {hasPermission('visits.create') && (
            <Button variant="contained" color="primary" startIcon={<Add />} onClick={openNewVisit}>
              {t('visitHistory.newVisit')}
            </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Patient Summary */}
      {currentPatient && (
        <Paper
          elevation={0}
          sx={{
            ...surfaceCardSx,
            borderRadius: 0,
            borderLeft: 0,
            borderRight: 0,
            borderTop: 0,
            boxShadow: 'none',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ px: 3, py: 3 }}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  hidden
                  onChange={handlePatientPhotoChange}
                />
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    sx={{
                      width: 88,
                      height: 88,
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: 'primary.main',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                    src={patientPhotoSrc || undefined}
                    imgProps={{ style: { objectFit: 'contain' } }}
                  >
                    {!patientPhotoSrc && <Person sx={{ fontSize: 40 }} />}
                  </Avatar>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={photoUploading}
                    onClick={() => photoInputRef.current?.click()}
                    title={t('visitHistory.uploadPhoto')}
                    sx={{
                      position: 'absolute',
                      right: -4,
                      bottom: -4,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: theme.shadows[2],
                      '&:hover': { bgcolor: 'background.paper' },
                    }}
                  >
                    <PhotoCamera fontSize="small" />
                  </IconButton>
                </Box>
                <Button
                  size="small"
                  variant="text"
                  disabled={photoUploading}
                  onClick={() => photoInputRef.current?.click()}
                  sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
                >
                  {photoUploading ? t('visitHistory.photoUploading') : t('visitHistory.changePhoto')}
                </Button>
                {patientHasCustomPhoto && (
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    disabled={photoUploading}
                    onClick={handleRemovePatientPhoto}
                    sx={{ textTransform: 'none', minWidth: 0, px: 1, color: 'text.secondary' }}
                  >
                    {t('visitHistory.removePhoto')}
                  </Button>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="h4" sx={{ color: 'text.primary', mb: 1 }}>
                      {currentPatient.name}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }}>
                      {t('visitHistory.dob')}: {medicalDetail?.dob ? formatDate(medicalDetail.dob) : t('visitHistory.na')} • ID: {currentPatient.id}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isEditingMedical && (
                      <Button size="small" variant="contained" onClick={saveMedicalDetail} disabled={medicalSaving}>
                        {medicalSaving ? t('visitHistory.saving') : t('visitHistory.save')}
                      </Button>
                    )}
                    <IconButton sx={{ color: 'text.secondary' }} onClick={() => setIsEditingMedical(prev => !prev)}>
                      <Edit />
                    </IconButton>
                  </Box>
                </Box>
                {photoMessage && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPhotoMessage('')}>
                    {photoMessage}
                  </Alert>
                )}
                {medicalError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {medicalError}
                  </Alert>
                )}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                      {t('visitHistory.contactInfo')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('visitHistory.phone')}: {currentPatient.phone}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                      {t('visitHistory.medicalSummary')}
                    </Typography>
                    {isEditingMedical ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        value={medicalForm.medical_summary}
                        onChange={(e) => setMedicalForm(prev => ({ ...prev, medical_summary: e.target.value }))}
                        sx={{
                          '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                        }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {medicalDetail?.medical_summary || t('visitHistory.noMedicalSummary')}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                      {t('visitHistory.allergiesNotes')}
                    </Typography>
                    {isEditingMedical ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        value={medicalForm.allergies}
                        onChange={(e) => setMedicalForm(prev => ({ ...prev, allergies: e.target.value }))}
                        sx={{
                          '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                        }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {medicalDetail?.allergies || t('visitHistory.noAllergies')}
                      </Typography>
                    )}
                  </Grid>
                  {isEditingMedical && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                        {t('visitHistory.dob')}
                      </Typography>
                      <TextField
                        fullWidth
                        type="date"
                        value={medicalForm.dob}
                        onChange={(e) => setMedicalForm(prev => ({ ...prev, dob: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          minHeight: 'calc(100vh - 280px)',
          gap: 2,
          p: 2,
          bgcolor: 'background.default',
        }}
      >
        {/* Timeline Sidebar */}
        <Paper
          elevation={0}
          sx={{
            ...surfaceCardSx,
            width: 300,
            flexShrink: 0,
            alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 300px)',
            overflow: 'auto',
            position: 'sticky',
            top: 16,
          }}
        >
          <Box sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
              {t('visitHistory.timeline')}
            </Typography>
            <List>
              {visits.map((visit) => {
                const pendingExamCount = getPendingExamCountForVisit(visit);
                const isSelected = selectedVisit?.id === visit.id;
                return (
                <ListItem key={visit.id} disablePadding sx={{ mb: 2 }}>
                  <ListItemButton
                    onClick={() => handleVisitSelect(visit)}
                    selected={isSelected}
                    sx={timelineItemSx(isSelected, pendingExamCount)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ color: 'text.primary', pr: 1 }}>
                        {visit.title || getVisitTypeName(visit)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                        {formatDate(visit.schedule)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      {visit.motivo || t('visitHistory.appointment.routineCheckup')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip
                        label={translateVisitStatus(getVisitStatusName(visit))}
                        size="small"
                        color={statusColors[getVisitStatusName(visit)] || 'default'}
                        sx={{ fontSize: '0.7rem' }}
                      />
                      {pendingExamCount > 0 && (
                        <Chip
                          icon={<Science sx={{ fontSize: '14px !important' }} />}
                          label={
                            pendingExamCount > 1
                              ? t('visitHistory.timelinePendingBadge', { count: pendingExamCount })
                              : t('visitHistory.timelinePendingBadgeShort')
                          }
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{
                            fontSize: '0.65rem',
                            '& .MuiChip-icon': { color: 'warning.main' },
                          }}
                        />
                      )}
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {providerName}
                      </Typography>
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
              })}
            </List>
          </Box>
        </Paper>

        {/* Visit Details */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
          {selectedVisit ? (
            <Box sx={{ maxWidth: 1040, mx: 'auto' }}>
              {/* Visit Header */}
              <Paper sx={{ ...surfaceCardSx, mb: 2, p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box>
                    <Typography variant="h5" sx={{ color: 'text.primary', mb: 1 }}>
                      {selectedVisit.title || getVisitTypeName(selectedVisit)} - {formatDate(selectedVisit.schedule)}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }}>
                      {providerName} • {formatTime(selectedVisit.schedule)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<Edit />}
                      onClick={startEditVisit}
                      disabled={selectedVisit?._source === 'appointment'}
                    >
                      {t('visitHistory.edit')}
                    </Button>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('visitHistory.visitType')}: <span style={{ color: 'text.primary' }}>{selectedVisit.type?.name || '-'}</span>
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('visitHistory.status')}:
                      <Chip
                        label={translateVisitStatus(getVisitStatusName(selectedVisit))}
                        size="small"
                        color={statusColors[getVisitStatusName(selectedVisit)] || 'default'}
                        sx={{ ml: 1, fontSize: '0.7rem' }}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('visitHistory.duration')}: <span style={{ color: 'text.primary' }}>{t('visitHistory.defaultDuration')}</span>
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Tabs */}
              <Paper sx={{ ...surfaceCardSx, mb: 2, overflow: 'hidden' }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    px: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    '& .MuiTab-root': { color: 'text.secondary', fontWeight: 500 },
                    '& .Mui-selected': { color: 'primary.main', fontWeight: 600 },
                  }}
                >
                  <Tab icon={<Assignment />} label={t('visitHistory.tabs.consultationNotes')} />
                  <Tab icon={<Science />} label={t('visitHistory.tabs.prescribedExams')} />
                  <Tab icon={<Medication />} label={t('visitHistory.tabs.prescription')} />
                  <Tab icon={<LocalHospital />} label={t('visitHistory.tabs.invoices')} />
                </Tabs>

                <TabPanel value={activeTab} index={0}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        {t('visitHistory.consultation.chiefComplaint')}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={selectedVisit.motivo || ''}
                        placeholder={t('visitHistory.consultation.chiefComplaintPlaceholder')}
                        sx={visitInputSx}
                        InputProps={{ readOnly: true }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        {t('visitHistory.consultation.physicalExamination')}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={selectedVisit.einicial || ''}
                        placeholder={t('visitHistory.consultation.physicalExaminationPlaceholder')}
                        sx={visitInputSx}
                        InputProps={{ readOnly: true }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        {t('visitHistory.consultation.assessmentPlan')}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={selectedVisit.conclusiones || ''}
                        placeholder={t('visitHistory.consultation.assessmentPlanPlaceholder')}
                        sx={visitInputSx}
                        InputProps={{ readOnly: true }}
                      />
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {examListAlert.message && (
                      <Alert severity={examListAlert.severity || 'info'}>
                        {examListAlert.message}
                      </Alert>
                    )}
                    {/* Add New Exam */}
                    <Paper sx={{ ...nestedPanelSx, p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                          {t('visitHistory.exams.addNewExam')}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={openNewExamForm}
                          color="primary"
                        >
                          {t('visitHistory.exams.addExam')}
                        </Button>
                      </Box>
                    </Paper>

                    {/* Current Prescribed Exams */}
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                        {t('visitHistory.exams.prescribedForVisit')}
                      </Typography>
                      {exams.length === 0 ? (
                        <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                          {t('visitHistory.exams.empty')}
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {exams.map((exam) => (
                            <Paper key={exam.id} sx={{ ...nestedPanelSx, p: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
                                      {exam.elist?.name || t('visitHistory.exams.unknownExam')}
                                    </Typography>
                                    <Chip
                                      label={translateExamStatus(exam.estatus?.name)}
                                      size="small"
                                      color={exam.estatus?.name === 'completed' ? 'success' : 'warning'}
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                    <Chip
                                      label={translateExamPriority(exam.etype?.name)}
                                      size="small"
                                      color={exam.etype?.name === 'Urgent' ? 'error' : 'default'}
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  </Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                    {exam.reason || t('visitHistory.exams.noReason')}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                    {t('visitHistory.exams.requested')}: {formatDate(exam.date)}
                                    {exam.notes && (
                                      <>
                                        <span style={{ margin: '0 8px' }}>•</span>
                                        {t('visitHistory.exams.notes')}: {exam.notes}
                                      </>
                                    )}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <IconButton
                                    onClick={() => editExam(exam)}
                                    sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                                  >
                                    <Edit />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => deleteExam(exam.id)}
                                    sx={{ color: 'text.secondary', '&:hover': { color: 'red' } }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Button
                        variant="outlined"
                        sx={{ borderColor: 'divider', color: 'text.secondary' }}
                      >
                        {t('visitHistory.exams.cancel')}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                      >
                        {t('visitHistory.exams.saveExams')}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Print />}
                        onClick={handlePrintExams}
                        disabled={selectedVisit?._source === 'appointment'}
                        sx={{ borderColor: 'divider', color: 'text.secondary' }}
                      >
                        {t('visitHistory.exams.printOrders')}
                      </Button>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Prescription Header */}
                    <Paper sx={{ ...nestedPanelSx, p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {renderClinicLogoBox(72)}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ color: 'text.primary', mb: 0.5 }}>
                            {t('visitHistory.prescriptionTab.title')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            {clinicLogoSrc
                              ? t('visitHistory.invoiceTab.logoFromSettings')
                              : t('visitHistory.invoiceTab.uploadLogoInSettings')}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box>
                          <Typography sx={{ color: 'text.secondary' }}>
                            {prescriberName} • {t('visitHistory.common.license')}: {prescriberLicense}
                          </Typography>
                          <Typography sx={{ color: 'text.secondary' }}>
                            {t('visitHistory.common.date')}: {formatDate(selectedVisit.schedule)}
                          </Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ borderColor: 'divider', my: 2 }} />
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                            {t('visitHistory.prescriptionTab.patientInfo')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            {currentPatient?.name || t('visitHistory.common.unknownPatient')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('visitHistory.dob')}: {formatDate(currentPatient?.created_at)} • {t('visitHistory.common.id')}: {currentPatient?.id}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                            {t('visitHistory.prescriptionTab.clinicInfo')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            {clinicInfo.name || t('visitHistory.prescriptionTab.clinicName')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {clinicInfo.address || t('visitHistory.prescriptionTab.clinicAddress')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('visitHistory.phone')}: {clinicInfo.phone || '(555) 987-6543'}
                          </Typography>
                          {clinicInfo.email && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {clinicInfo.email}
                            </Typography>
                          )}
                          {clinicInfo.nit && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {t('settings.clinicNit')}: {clinicInfo.nit}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Add New Medication */}
                    <Paper sx={{ ...nestedPanelSx, p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                          {t('visitHistory.prescriptionTab.addNewMedication')}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={openNewPrescriptionForm}
                          color="primary"
                        >
                          {t('visitHistory.prescriptionTab.addMedication')}
                        </Button>
                      </Box>
                    </Paper>

                    {/* Current Prescriptions */}
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                        {t('visitHistory.prescriptionTab.currentPrescriptions')}
                      </Typography>
                      {prescriptions.length === 0 ? (
                        <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                          {t('visitHistory.prescriptionTab.empty')}
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {prescriptions.map((prescription) => (
                            <Paper key={prescription.id} sx={{ ...nestedPanelSx, p: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
                                      {prescription.medication}
                                    </Typography>
                                    <Chip
                                      label={translatePrescriptionStatus(prescription.status)}
                                      size="small"
                                      color={prescription.status === 'active' ? 'success' : 'default'}
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  </Box>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      {t('visitHistory.prescriptionTab.dosage')}:{' '}
                                      <span style={{ color: 'text.primary' }}>
                                        {formatPrescriptionDoseDisplay(prescription) || t('visitHistory.na')}
                                      </span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      {t('visitHistory.prescriptionTab.frequency')}:{' '}
                                      <span style={{ color: 'text.primary' }}>{prescription.frequency || t('visitHistory.na')}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      {t('visitHistory.prescriptionTab.duration')}:{' '}
                                      <span style={{ color: 'text.primary' }}>{prescription.duration || t('visitHistory.na')}</span>
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                    {prescription.instructions || t('visitHistory.prescriptionTab.noInstructions')}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <IconButton
                                    onClick={() => editPrescription(prescription)}
                                    sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                                  >
                                    <Edit />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => deletePrescription(prescription.id)}
                                    sx={{ color: 'text.secondary', '&:hover': { color: 'red' } }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Additional Notes */}
                    <Paper sx={{ ...nestedPanelSx, p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                        {t('visitHistory.prescriptions.additionalNotesTitle')}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={visitPrescriptionNotes}
                        onChange={(e) => setVisitPrescriptionNotes(e.target.value)}
                        placeholder={t('visitHistory.prescriptions.additionalNotesPlaceholder')}
                        disabled={selectedVisit?._source === 'appointment'}
                        sx={{
                          '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                        }}
                      />
                    </Paper>

                    {/* Drug Interactions & Warnings */}
                    <Paper sx={{ ...nestedPanelSx, p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                          {t('visitHistory.prescriptions.drugWarningsTitle')}
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={addDrugWarning}
                          disabled={selectedVisit?._source === 'appointment'}
                          sx={{ borderColor: 'divider', color: 'text.secondary' }}
                        >
                          {t('visitHistory.prescriptions.addWarning')}
                        </Button>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {visitDrugWarnings.length === 0 ? (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('visitHistory.prescriptions.noWarnings')}
                          </Typography>
                        ) : (
                          visitDrugWarnings.map((warning) => (
                            <Box
                              key={warning.id}
                              sx={{ display: 'flex', gap: 2, p: 2, ...nestedPanelSx }}
                            >
                              <Warning sx={{ color: 'text.secondary', mt: 1 }} />
                              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label={t('visitHistory.prescriptions.warningTitle')}
                                  value={warning.title}
                                  onChange={(e) => updateDrugWarning(warning.id, 'title', e.target.value)}
                                  disabled={selectedVisit?._source === 'appointment'}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{
                                    '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                                  }}
                                />
                                <TextField
                                  fullWidth
                                  size="small"
                                  label={t('visitHistory.prescriptions.warningDetail')}
                                  value={warning.detail}
                                  onChange={(e) => updateDrugWarning(warning.id, 'detail', e.target.value)}
                                  disabled={selectedVisit?._source === 'appointment'}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{
                                    '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                                  }}
                                />
                              </Box>
                              <IconButton
                                onClick={() => removeDrugWarning(warning.id)}
                                disabled={selectedVisit?._source === 'appointment'}
                                sx={{ color: 'text.secondary', alignSelf: 'flex-start', '&:hover': { color: 'red' } }}
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          ))
                        )}
                      </Box>
                    </Paper>

                    {(prescriptionContextMessage || prescriptionContextError) && (
                      <Alert severity={prescriptionContextError ? 'error' : 'success'}>
                        {prescriptionContextError || prescriptionContextMessage}
                      </Alert>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="contained"
                          onClick={savePrescriptionContext}
                          disabled={
                            prescriptionContextSaving || selectedVisit?._source === 'appointment'
                          }
                          color="primary"
                        >
                          {prescriptionContextSaving
                            ? t('visitHistory.prescriptions.saving')
                            : t('visitHistory.prescriptions.saveNotes')}
                        </Button>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={handlePreviewPrescriptions}
                          disabled={selectedVisit?._source === 'appointment'}
                          sx={{ borderColor: 'divider', color: 'text.secondary' }}
                        >
                          {t('visitHistory.common.preview')}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<Print />}
                          onClick={handlePrintPrescriptions}
                          disabled={selectedVisit?._source === 'appointment'}
                          sx={{ borderColor: 'divider', color: 'text.secondary' }}
                        >
                          {t('visitHistory.prescriptionTab.printPrescription')}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Invoice Header — logo + date only */}
                    <Paper sx={{ ...nestedPanelSx, p: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'flex-end',
                          gap: 3,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 240px' }}>
                          {renderClinicLogoBox(64)}
                          <Box>
                            <Typography variant="h6" sx={{ color: 'text.primary' }}>
                              {t('visitHistory.invoiceTab.logoTitle')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {clinicLogoSrc
                                ? t('visitHistory.invoiceTab.logoFromSettings')
                                : t('visitHistory.invoiceTab.uploadLogoInSettings')}
                            </Typography>
                          </Box>
                        </Box>
                        <TextField
                          label={t('visitHistory.invoiceTab.invoiceDate')}
                          type="date"
                          value={invoiceForm.header.date}
                          onChange={(e) => handleInvoiceFormChange('header', 'date', e.target.value)}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            flex: '0 1 220px',
                            minWidth: 180,
                            '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                            '& .MuiInputLabel-root': { color: 'text.secondary' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                          }}
                        />
                      </Box>
                    </Paper>

                    {/* Patient Information */}
                    <Paper sx={{ ...nestedPanelSx, p: 3 }}>
                      <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                        {t('visitHistory.invoiceTab.patientInfo')}
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                              label={t('visitHistory.invoiceTab.fullName')}
                              value={currentPatient?.name || ''}
                              fullWidth
                              size="small"
                              sx={{
                                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                                '& .MuiInputLabel-root': { color: 'text.secondary' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                              }}
                            />
                            <TextField
                              label={t('visitHistory.invoiceTab.address')}
                              multiline
                              rows={2}
                              value="456 Patient Street\nApt 2B, City, State 54321"
                              fullWidth
                              size="small"
                              sx={{
                                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                                '& .MuiInputLabel-root': { color: 'text.secondary' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                              label={t('visitHistory.invoiceTab.clientNit')}
                              value="987654321-5"
                              fullWidth
                              size="small"
                              sx={{
                                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                                '& .MuiInputLabel-root': { color: 'text.secondary' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                              }}
                            />
                            <TextField
                              label={t('visitHistory.invoiceTab.email')}
                              value="maria.rodriguez@email.com"
                              fullWidth
                              size="small"
                              sx={{
                                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                                '& .MuiInputLabel-root': { color: 'text.secondary' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                              }}
                            />
                            <TextField
                              label={t('visitHistory.invoiceTab.phone')}
                              value="(555) 123-4567"
                              fullWidth
                              size="small"
                              sx={{
                                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                                '& .MuiInputLabel-root': { color: 'text.secondary' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Service Details */}
                    <Paper sx={{ ...nestedPanelSx, p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                          {t('visitHistory.invoiceTab.serviceDetails')}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={openNewInvoiceForm}
                          color="primary"
                        >
                          {t('visitHistory.invoiceTab.newInvoice')}
                        </Button>
                      </Box>
                      
                      {/* Invoice Items */}
                      {(!invoices || !Array.isArray(invoices) || invoices.length === 0) ? (
                        <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                          {t('visitHistory.invoiceTab.empty')}
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {Array.isArray(invoices) && invoices.map((invoice) => (
                            <Paper key={invoice.id} sx={{ ...nestedPanelSx, p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
                                    {t('visitHistory.invoiceTab.invoiceNumberShort', { id: invoice.id })}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('visitHistory.invoiceTab.dateLine')}: {formatDate(invoice.date)} • {t('visitHistory.invoiceTab.nit')}: {invoice.nit}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('visitHistory.common.auth')}: {invoice.authorization} • {t('visitHistory.common.client')}: {invoice.client?.name || t('visitHistory.na')}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <IconButton
                                    onClick={() => editInvoice(invoice)}
                                    sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                                  >
                                    <Edit />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => deleteInvoice(invoice.id)}
                                    sx={{ color: 'text.secondary', '&:hover': { color: 'red' } }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      )}
                    </Paper>

                    {/* Invoice Summary */}
                    <Paper sx={{ ...nestedPanelSx, p: 3 }}>
                      <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
                        {t('visitHistory.invoiceTab.summary')}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t('visitHistory.invoiceTab.totalItems')}: {invoiceForm.details.length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t('visitHistory.invoiceTab.visitLabel')}: {selectedVisit?.title || t('visitHistory.na')}
                        </Typography>
                      </Box>
                    </Paper>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Button
                        variant="outlined"
                        sx={{ borderColor: 'divider', color: 'text.secondary' }}
                      >
                        {t('visitHistory.common.saveDraft')}
                      </Button>
                      <Button
                        variant="outlined"
                        sx={{ borderColor: 'divider', color: 'text.secondary' }}
                      >
                        {t('visitHistory.common.preview')}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                      >
                        {t('visitHistory.invoiceTab.generateInvoice')}
                      </Button>
                    </Box>
                  </Box>
                </TabPanel>
              </Paper>

              {pendingExams.length > 0 && (
                <Alert
                  icon={<Warning />}
                  severity="warning"
                  sx={{
                    ...surfaceCardSx,
                    color: 'text.primary',
                    '& .MuiAlert-icon': { color: 'warning.main' },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 1 }}>
                    {t('visitHistory.pendingExams.title')}
                  </Typography>
                  {pendingExams.map((exam) => (
                    <Typography key={exam.id} variant="body2" sx={{ mb: 0.5 }}>
                      {t('visitHistory.pendingExams.line', {
                        exam: exam.elist?.name || t('visitHistory.exams.unknownExam'),
                        date: formatDate(exam.date || exam.visit?.schedule || exam.created_at),
                      })}
                    </Typography>
                  ))}
                </Alert>
              )}
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              color: 'text.secondary',
              gap: 2
            }}>
              <Typography variant="h6">{t('visitHistory.selectVisitPrompt')}</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={openNewVisit}>
                {t('visitHistory.createFirstVisit')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* New/Edit Visit Dialog */}
      <Dialog 
        open={newVisitOpen} 
        onClose={() => {
          setNewVisitOpen(false);
          setVisitError('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { bgcolor: 'background.default', color: 'text.primary' }
        }}
      >
        <DialogTitle>
          {editMode ? t('visitHistory.form.editVisit') : t('visitHistory.form.newVisit')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {visitError && <Alert severity="error">{visitError}</Alert>}
            <TextField
              name="title"
              label={t('visitHistory.form.visitTitle')}
              value={visitForm.title}
              onChange={handleFormChange}
              required
              fullWidth
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            />
            <TextField
              name="schedule"
              label={t('visitHistory.form.schedule')}
              type="datetime-local"
              value={visitForm.schedule}
              onChange={handleFormChange}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            />
            <TextField
              name="next_appointment_date"
              label={t('visitHistory.form.nextAppointment')}
              type="date"
              value={visitForm.next_appointment_date}
              onChange={handleFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={t('visitHistory.form.nextAppointmentHint')}
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            />
            <TextField
              name="motivo"
              label={`${t('visitHistory.consultation.chiefComplaint')} (${t('visitHistory.form.optional')})`}
              multiline
              rows={3}
              value={visitForm.motivo}
              onChange={handleFormChange}
              fullWidth
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            />
            <TextField
              name="einicial"
              label={`${t('visitHistory.consultation.physicalExamination')} (${t('visitHistory.form.optional')})`}
              multiline
              rows={3}
              value={visitForm.einicial}
              onChange={handleFormChange}
              fullWidth
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            />
            <TextField
              name="conclusiones"
              label={`${t('visitHistory.consultation.assessmentPlan')} (${t('visitHistory.form.optional')})`}
              multiline
              rows={3}
              value={visitForm.conclusiones}
              onChange={handleFormChange}
              fullWidth
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setNewVisitOpen(false);
              setVisitError('');
            }}
            sx={{ color: 'text.secondary' }}
            disabled={visitSaving}
          >
            Cancel
          </Button>
          <Button 
            onClick={saveVisit}
            variant="contained"
            color="primary"
            disabled={visitSaving}
          >
            {visitSaving
              ? t('visitHistory.saving')
              : editMode
                ? t('visitHistory.form.updateVisit')
                : t('visitHistory.form.saveVisit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New/Edit Exam Dialog */}
      <Dialog 
        open={newExamOpen} 
        onClose={() => setNewExamOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: visitDialogPaperSx }}
      >
        <DialogTitle sx={{ flexShrink: 0, bgcolor: 'background.default', color: 'text.primary' }}>
          {editingExam ? t('visitHistory.exams.editExam') : t('visitHistory.exams.newExam')}
        </DialogTitle>
        <DialogContent dividers sx={{ ...visitDialogContentSx, pt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 1,
              width: '100%',
              minWidth: 0,
            }}
          >
            {examFormError && (
              <Alert severity="error">{examFormError}</Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                <Autocomplete
                  fullWidth
                  disablePortal={false}
                  freeSolo
                  options={examTypes}
                  value={selectedExamType}
                  onChange={handleExamTypeChange}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      modifiers: examAutocompletePopperModifiers,
                      sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                    },
                    paper: {
                      sx: {
                        width: '100%',
                        minWidth: '100%',
                        bgcolor: 'background.default',
                        color: 'text.primary',
                      },
                    },
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filterExamTypeOptions(options, params);
                    const input = params.inputValue.trim();
                    if (
                      input &&
                      !options.some(
                        (option) => option.name.toLowerCase() === input.toLowerCase()
                      )
                    ) {
                      filtered.push({
                        inputValue: input,
                        name: t('visitHistory.exams.addExamTypeOption', { name: input }),
                      });
                    }
                    return filtered;
                  }}
                  getOptionLabel={getExamTypeOptionLabel}
                  isOptionEqualToValue={(option, value) =>
                    option?.id != null && value?.id != null && option.id === value.id
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.id || `new-${option.inputValue}`}>
                      {option.inputValue
                        ? t('visitHistory.exams.addExamTypeOption', { name: option.inputValue })
                        : option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label={t('visitHistory.exams.examType')}
                      placeholder={t('visitHistory.exams.searchExamType')}
                      InputLabelProps={{ shrink: true, ...params.InputLabelProps }}
                    />
                  )}
                  ListboxProps={{
                    sx: {
                      width: '100%',
                      bgcolor: 'background.default',
                      color: 'text.primary',
                      '& .MuiAutocomplete-option': {
                        whiteSpace: 'normal',
                        '&:hover': { bgcolor: 'background.paper' },
                        '&[aria-selected="true"]': { bgcolor: 'background.paper' },
                      },
                    },
                  }}
                  sx={examSelectFieldSx}
                />
                <TextField
                  name="id_etype"
                  label={t('visitHistory.exams.priority')}
                  select
                  value={examForm.id_etype === '' || examForm.id_etype == null ? '' : String(examForm.id_etype)}
                  onChange={handleExamFormChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  SelectProps={examSelectMenuProps}
                  sx={examSelectFieldSx}
                >
                  <MenuItem value="">
                    <em>{t('visitHistory.exams.selectPriority')}</em>
                  </MenuItem>
                  <MenuItem value="1">{t('visitHistory.exams.priorities.urgent')}</MenuItem>
                  <MenuItem value="2">{t('visitHistory.exams.priorities.routine')}</MenuItem>
                  <MenuItem value="3">{t('visitHistory.exams.priorities.specific')}</MenuItem>
                  <MenuItem value="4">{t('visitHistory.exams.priorities.most')}</MenuItem>
                </TextField>
                <TextField
                  name="id_estatus"
                  label={t('visitHistory.exams.status')}
                  select
                  value={examForm.id_estatus === '' || examForm.id_estatus == null ? '' : String(examForm.id_estatus)}
                  onChange={handleExamFormChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  SelectProps={examSelectMenuProps}
                  sx={examSelectFieldSx}
                >
                  <MenuItem value={EXAM_STATUS_ID.ORDERED}>
                    {t('visitHistory.exams.statuses.ordered')}
                  </MenuItem>
                  <MenuItem value={EXAM_STATUS_ID.COMPLETED}>
                    {t('visitHistory.exams.statuses.completed')}
                  </MenuItem>
                </TextField>
                <TextField
                  name="date"
                  label={t('visitHistory.exams.requestedDate')}
                  type="date"
                  value={examForm.date}
                  onChange={handleExamFormChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                  }}
                />
            </Box>

            <TextField
              name="reason"
              label={t('visitHistory.exams.reason')}
              multiline
              rows={2}
              value={examForm.reason}
              onChange={handleExamFormChange}
              fullWidth
              placeholder={t('visitHistory.exams.reasonPlaceholder')}
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            />

            <TextField
              name="notes"
              label={t('visitHistory.exams.resultNotes')}
              multiline
              rows={3}
              value={examForm.notes}
              onChange={handleExamFormChange}
              fullWidth
              helperText={t('visitHistory.exams.notesAutoCompleteStatus')}
              placeholder={t('visitHistory.exams.resultNotesPlaceholder')}
              FormHelperTextProps={{ sx: { color: 'text.disabled' } }}
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
            />

          </Box>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, bgcolor: 'background.default' }}>
          <Button 
            onClick={() => setNewExamOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            {t('visitHistory.exams.cancel')}
          </Button>
          <Button 
            onClick={saveExam}
            variant="contained"
            color="primary"
          >
            {editingExam ? t('visitHistory.exams.updateExam') : t('visitHistory.exams.saveExam')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New/Edit Prescription Dialog */}
      <Dialog 
        open={newPrescriptionOpen} 
        onClose={() => setNewPrescriptionOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: visitDialogPaperSx }}
      >
        <DialogTitle sx={{ flexShrink: 0, bgcolor: 'background.default', color: 'text.primary' }}>
          {editingPrescription
            ? t('visitHistory.prescriptionTab.dialog.editTitle')
            : t('visitHistory.prescriptionTab.dialog.newTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ ...visitDialogContentSx, pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <TextField
              name="medication"
              label={t('visitHistory.prescriptionTab.dialog.medicationName')}
              value={prescriptionForm.medication}
              onChange={handlePrescriptionFormChange}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              placeholder={t('visitHistory.prescriptionTab.dialog.medicationPlaceholder')}
              sx={examSelectFieldSx}
            />
            <TextField
              name="dose"
              label={t('visitHistory.prescriptionTab.dialog.dosage')}
              value={prescriptionForm.dose}
              onChange={handlePrescriptionFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              placeholder={t('visitHistory.prescriptionTab.dialog.dosagePlaceholder')}
              helperText={t('visitHistory.prescriptionTab.dialog.dosageHelper')}
              FormHelperTextProps={{ sx: { color: 'text.disabled' } }}
              sx={examSelectFieldSx}
            />
            <TextField
              name="frequency"
              label={t('visitHistory.prescriptionTab.dialog.frequency')}
              value={prescriptionForm.frequency}
              onChange={handlePrescriptionFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              placeholder={t('visitHistory.prescriptionTab.dialog.frequencyPlaceholder')}
              sx={examSelectFieldSx}
            />
            <TextField
              name="duration"
              label={t('visitHistory.prescriptionTab.dialog.duration')}
              value={prescriptionForm.duration}
              onChange={handlePrescriptionFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              placeholder={t('visitHistory.prescriptionTab.dialog.durationPlaceholderDays')}
              sx={examSelectFieldSx}
            />
            <TextField
              name="instructions"
              label={t('visitHistory.prescriptionTab.dialog.instructions')}
              multiline
              rows={2}
              value={prescriptionForm.instructions}
              onChange={handlePrescriptionFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              placeholder={t('visitHistory.prescriptionTab.dialog.instructionsPlaceholder')}
              sx={examSelectFieldSx}
            />
            <TextField
              name="extra_notes"
              label={t('visitHistory.prescriptionTab.dialog.extraNotes')}
              multiline
              rows={2}
              value={prescriptionForm.extra_notes}
              onChange={handlePrescriptionFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              placeholder={t('visitHistory.prescriptionTab.dialog.extraNotesPlaceholder')}
              sx={examSelectFieldSx}
            />
            <TextField
              name="status"
              label={t('visitHistory.prescriptionTab.dialog.status')}
              select
              value={prescriptionForm.status}
              onChange={handlePrescriptionFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={examSelectMenuProps}
              sx={examSelectFieldSx}
            >
              <MenuItem value="active">{t('visitHistory.prescriptionTab.medicationStatuses.active')}</MenuItem>
              <MenuItem value="completed">{t('visitHistory.prescriptionTab.medicationStatuses.completed')}</MenuItem>
              <MenuItem value="cancelled">{t('visitHistory.prescriptionTab.medicationStatuses.cancelled')}</MenuItem>
              <MenuItem value="extended">{t('visitHistory.prescriptionTab.medicationStatuses.extended')}</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, bgcolor: 'background.default' }}>
          <Button 
            onClick={() => setNewPrescriptionOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            {t('visitHistory.prescriptionTab.dialog.cancel')}
          </Button>
          <Button 
            onClick={savePrescription}
            variant="contained"
            color="primary"
          >
            {editingPrescription
              ? t('visitHistory.prescriptionTab.dialog.update')
              : t('visitHistory.prescriptionTab.dialog.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New/Edit Invoice Dialog */}
      <Dialog 
        open={newInvoiceOpen} 
        onClose={() => setNewInvoiceOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: visitDialogPaperSx }}
      >
        <DialogTitle sx={{ flexShrink: 0, bgcolor: 'background.default', color: 'text.primary' }}>
          {editingInvoice
            ? t('visitHistory.invoiceTab.dialog.editTitle')
            : t('visitHistory.invoiceTab.dialog.createTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ ...visitDialogContentSx, pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Invoice Header */}
            <Paper sx={{ ...nestedPanelSx, p: 3 }}>
              <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                {t('visitHistory.invoiceTab.dialog.headerTitle')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('visitHistory.invoiceTab.invoiceDate')}
                    type="date"
                    value={invoiceForm.header.date}
                    onChange={(e) => handleInvoiceFormChange('header', 'date', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                      '& .MuiInputLabel-root': { color: 'text.secondary' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('visitHistory.invoiceTab.dueDate')}
                    type="date"
                    value={invoiceForm.header.due_date}
                    onChange={(e) => handleInvoiceFormChange('header', 'due_date', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                      '& .MuiInputLabel-root': { color: 'text.secondary' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('visitHistory.invoiceTab.dialog.status')}
                    select
                    value={invoiceForm.header.status}
                    onChange={(e) => handleInvoiceFormChange('header', 'status', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                      '& .MuiInputLabel-root': { color: 'text.secondary' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    }}
                  >
                    <MenuItem value="draft">{t('visitHistory.invoiceTab.dialog.invoiceStatuses.draft')}</MenuItem>
                    <MenuItem value="sent">{t('visitHistory.invoiceTab.dialog.invoiceStatuses.sent')}</MenuItem>
                    <MenuItem value="paid">{t('visitHistory.invoiceTab.dialog.invoiceStatuses.paid')}</MenuItem>
                    <MenuItem value="overdue">{t('visitHistory.invoiceTab.dialog.invoiceStatuses.overdue')}</MenuItem>
                    <MenuItem value="cancelled">{t('visitHistory.invoiceTab.dialog.invoiceStatuses.cancelled')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('visitHistory.invoiceTab.dialog.paymentTerms')}
                    value={invoiceForm.header.payment_terms}
                    onChange={(e) => handleInvoiceFormChange('header', 'payment_terms', e.target.value)}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                      '& .MuiInputLabel-root': { color: 'text.secondary' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('visitHistory.invoiceTab.dialog.notes')}
                    multiline
                    rows={2}
                    value={invoiceForm.header.notes}
                    onChange={(e) => handleInvoiceFormChange('header', 'notes', e.target.value)}
                    fullWidth
                    placeholder={t('visitHistory.invoiceTab.dialog.notesPlaceholder')}
                    sx={{
                      '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                      '& .MuiInputLabel-root': { color: 'text.secondary' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Invoice Details */}
            <Paper sx={{ ...nestedPanelSx, p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'text.primary' }}>
                  {t('visitHistory.invoiceTab.dialog.detailsTitle')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addInvoiceDetail}
                  sx={{ borderColor: 'divider', color: 'text.secondary' }}
                >
                  {t('visitHistory.invoiceTab.dialog.addItem')}
                </Button>
              </Box>
              
              {/* Invoice Items Table */}
              <Box
                key={`invoice-lines-${invoiceCurrencyKey}`}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                {/* Header Row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr auto', gap: 2, p: 1, ...nestedPanelSx }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>{t('visitHistory.invoiceTab.dialog.description')}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>{t('visitHistory.invoiceTab.dialog.quantity')}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>{t('visitHistory.invoiceTab.dialog.unitPrice')}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>{t('visitHistory.invoiceTab.dialog.discount')}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>{t('visitHistory.invoiceTab.dialog.total')}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>{t('visitHistory.invoiceTab.dialog.actions')}</Typography>
                </Box>

                {/* Item Rows */}
                {invoiceForm.details.map((detail, index) => (
                  <Box key={index} sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr auto', gap: 2, alignItems: 'center' }}>
                    <TextField
                      label={t('visitHistory.invoiceTab.dialog.description')}
                      value={detail.descripcion}
                      onChange={(e) => handleInvoiceFormChange('details', 'descripcion', e.target.value, index)}
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                        '& .MuiInputLabel-root': { color: 'text.secondary' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                      }}
                    />
                    <TextField
                      label={t('visitHistory.invoiceTab.dialog.quantity')}
                      type="number"
                      value={detail.cantidad}
                      onChange={(e) => handleInvoiceFormChange('details', 'cantidad', parseInt(e.target.value) || 0, index)}
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                        '& .MuiInputLabel-root': { color: 'text.secondary' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                      }}
                    />
                    <TextField
                      label={t('visitHistory.invoiceTab.dialog.unitPrice')}
                      type="number"
                      step="0.01"
                      value={detail.precio}
                      onChange={(e) => handleInvoiceFormChange('details', 'precio', parseFloat(e.target.value) || 0, index)}
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                        '& .MuiInputLabel-root': { color: 'text.secondary' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                      }}
                    />
                    <TextField
                      label={t('visitHistory.invoiceTab.dialog.discount')}
                      type="number"
                      step="0.01"
                      value={detail.descuento}
                      onChange={(e) => handleInvoiceFormChange('details', 'descuento', parseFloat(e.target.value) || 0, index)}
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': { bgcolor: 'background.paper', color: 'text.primary' },
                        '& .MuiInputLabel-root': { color: 'text.secondary' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                      }}
                    />
                    <Typography variant="body2" sx={{ color: 'text.primary', textAlign: 'center' }}>
                      {formatInvoiceAmount(getInvoiceLineTotal(detail))}
                    </Typography>
                    <IconButton
                      onClick={() => removeInvoiceDetail(index)}
                      disabled={invoiceForm.details.length === 1}
                      sx={{ color: 'red', '&:disabled': { color: 'text.disabled' } }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              {/* Totals — same logic as invoices module (no tax) */}
              <Box
                key={invoiceCurrencyKey}
                sx={{ mt: 3, p: 2, ...nestedPanelSx, maxWidth: 360, ml: 'auto' }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('visitHistory.invoiceTab.dialog.subtotal')}:</Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {formatInvoiceAmount(calculateInvoiceTotals().subtotal)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('common.discount')}:</Typography>
                  <Typography variant="body2" sx={{ color: 'error.light' }}>
                    -{formatInvoiceAmount(calculateInvoiceTotals().totalDiscount)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>{t('visitHistory.invoiceTab.dialog.grandTotal')}:</Typography>
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    {formatInvoiceAmount(calculateInvoiceTotals().total)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, bgcolor: 'background.default' }}>
          <Button 
            onClick={() => setNewInvoiceOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            {t('visitHistory.invoiceTab.dialog.cancel')}
          </Button>
          <Button 
            onClick={saveInvoice}
            variant="contained"
            color="primary"
          >
            {editingInvoice
              ? t('visitHistory.invoiceTab.dialog.updateInvoice')
              : t('visitHistory.invoiceTab.dialog.saveInvoice')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
